'use client';

import type { SanityImage } from '../types';
import { tv } from '../_lib/utils';
import { Media } from '../Media';
import type { brandValues, leftRightValues } from '../_lib/designTypesStore';
import { TextSectionBlock, TextSectionBlockProps } from '../TextSectionBlock';
import { useBrandValue, useThemeValue } from '../Theme';

const styles = tv({
	slots: {
		base:
			'relative isolate bg-background-primary flex flex-col items-center py-6 max-md:gap-4 md:py-0 md:flex-row md:aspect-2/1 rounded-[1.5rem] max-h-[75dvh] md:max-h-[80dvh]' +
			' ' +
			'after:absolute after:w-full after:h-full after:top-0 after:left-0 after:rounded-[1.5rem]',
		media: 'relative z-1 overflow-hidden w-full md:absolute h-full md:w-[60%] md:aspect-6/4 [&_img]:h-full lg:w-[55%]',
		text: 'relative flex items-center px-6 z-1 h-full w-full md:h-auto md:p-8 md:w-[40%] md:overflow-auto lg:p-responsive-4xl lg:w-[45%]',
	},
	variants: {
		contrast: {
			true: {},
			false: {},
		},
		bgColour: {
			neutral: {},
			brand: {
				base: 'after:bg-card-background-brand',
			},
		},
		textPosition: {
			left: {
				media: '-mb-6 md:mb-0 rounded-b-[1.5rem] rounded-t-none md:rounded-r-[1.5rem] md:rounded-bl-none bottom-0 right-0',
			},
			right: {
				media: '-mb-6 md:mb-0 rounded-b-[1.5rem] rounded-t-none md:rounded-l-[1.5rem] md:rounded-br-none bottom-0 left-0',
				text: 'md:ml-auto',
			},
		},
	},
	compoundVariants: [
		{
			contrast: true,
			bgColour: 'neutral',
			className: 'after:bg-card-background-primary',
		},
		{
			contrast: false,
			bgColour: 'neutral',
			className: 'after:bg-card-background-secondary',
		},
	],
});

export type CardsStackItemProps = {
	className?: string;
	textSectionBlock?: Omit<TextSectionBlockProps, 'size'>;
	image?: SanityImage;
	video?: string;
	theme?: 'light' | 'dark';
	brand?: (typeof brandValues)[number];
	backgroundColour?: 'neutral' | 'brand';
	textPosition?: (typeof leftRightValues)[number];
};

export function CardsStackItem(props: CardsStackItemProps) {
	const globalTheme = useThemeValue();
	const globalBrand = useBrandValue();

	const textPosition = props.textPosition ?? 'left';
	const theme = props.theme ?? globalTheme;
	const brand = props.brand ?? globalBrand;
	const bgColour = props.backgroundColour ?? 'neutral';
	const contrast = theme !== globalTheme;

	const { base, media, text } = styles({ textPosition, contrast, bgColour });

	return (
		<article
			data-component='CardsStackItem'
			className={base({ className: props.className })}
			data-theme={theme}
			data-brand={brand}
			data-header-ignore
		>
			<div className={text()}>{props.textSectionBlock && <TextSectionBlock size='sm' {...props.textSectionBlock} />}</div>

			{props.image && <Media className={media()} image={props.image} video={props.video} />}
		</article>
	);
}
